import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import copy from 'copy-to-clipboard';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import classnames from 'classnames';
import {
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal, showModalTop } from '../../actions';
import { showConfirmationModal } from '../../actions/App';
import {
  getCollection,
  showCollectionAddModal,
  removeFilesFromCollection,
  setSidebarCollection,
  addFilesToSidebarCollectionFilesList,
  removeFilesFromSidebarCollectionFilesList,
} from '../../actions/Collections';
import {
  setSelectedFile,
  showUploadModal,
  setUploadModalMode,
  deleteFile,
  removeFilesFromFilesList,
  updateFilesOnFilesList,
  updateFile,
  unsetSelectedFile,
  restoreDeletedFile,
  deleteFilePermanent,
} from '../../actions/Files';

import {
  APIaddFilesToCollection,
  APIsetCollectionFeaturedImage,
} from '../../api/Collections';
import {
  APIcreateFilesGroup,
  APIgetFilePublicUrl,
  APIchangeFileThumbnail,
  APIremoveFileThumbnail,
  APIrefreshFileThumbnail,
} from '../../api/Files';
import { APIgetShareFilePublicUrl } from '../../api/Shares';

import { isArchive } from '../../helpers/App';
import {
  userCan,
  userCanSeeComments,
  userHasCap,
  userIsAdmin,
  userIsManager,
} from '../../helpers/Auth';
import { userHasFeature } from '../../helpers/Features';
import { isEditionInWorkspace, printFile } from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';
import { upgradeModalIfNeeded } from '../../helpers/UpgradePlan';

import FileActionsIntegrations from './ActionsIntegrations';
import FileExpirationIcon from './ExpirationIcon';
import GridItemLikes from '../GridItemLikes';

import ErrorBoundary from '../ErrorBoundary';
import ProcessingSpinner from '../ProcessingSpinner';
import UpgradePlanButton from '../UpgradePlanButton';

class FileActions extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      watermarkedChangeLoading: false,
    };

    this.handleRemoveFromCollection = this.handleRemoveFromCollection.bind(this);
    this.handleWatermarkedChangeLoading = this.handleWatermarkedChangeLoading.bind(this);
  }

  isFileInSidebarCollection(fileUuid) {
    const { sidebarCollectionFiles } = this.props;
    return sidebarCollectionFiles.find(scf => (scf.uuid === fileUuid));
  }

  handleCopyFilePublicUrl(file) {
    const { intl } = this.props;

    APIgetFilePublicUrl(file.uuid, {
      cf: (getSetting('allow_public_urls') || userHasCap('public_file_urls')) ? true : undefined,
    })
      .then((response) => {
        const { data } = response;
        if (data && data.url) {
          copy(data.url);
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'File public URL has been copied' }),
          });
        }
      });
  }

  handleRemoveFromCollection(file) {
    const {
      intl,
      openConfirmationModal,
      collection,
      deleteFilesFromCollection,
    } = this.props;

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({
        defaultMessage: 'Do you want to remove this file from {collectionName} Collection?',
      }, { collectionName: collection.name }),
      confirmText: intl.formatMessage({ defaultMessage: 'Remove' }),
      processingText: intl.formatMessage({ defaultMessage: 'Removing' }),
      onConfirm: () => (
        deleteFilesFromCollection(collection.uuid, {
          files: [file.uuid],
        })
      ),
    };
    openConfirmationModal(file, modalParams);
  }

  handleDeleteFile(file) {
    const {
      intl,
      openConfirmationModal,
      removeFile,
      unselectFile,
    } = this.props;

    const mtext = (file.versions && file.versions.length > 0) ?
      intl.formatMessage({ defaultMessage: 'Are you sure you want to delete all versions of this file?' }) :
      intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this file?' });

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({
        defaultMessage: `{mtext} Files can be restored from the trash.`,
      }, { mtext }),
      confirmText: intl.formatMessage({ defaultMessage: 'Delete' }),
      processingText: intl.formatMessage({ defaultMessage: 'Deleting' }),
      onConfirm: () => (
        removeFile(file.uuid)
      ),
      onCancel: () => {
        unselectFile();
        return Promise.resolve();
      },
    };
    openConfirmationModal(file, modalParams);
  }

  handleRestoreDeletedFile(file) {
    const {
      intl,
      openConfirmationModal,
      restoreFile,
    } = this.props;

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({
        defaultMessage: 'Are you sure you want to restore this file?',
      }),
      confirmText: intl.formatMessage({ defaultMessage: 'Restore' }),
      processingText: intl.formatMessage({ defaultMessage: 'Restoring' }),
      onConfirm: () => restoreFile(file.uuid),
    };
    openConfirmationModal(file, modalParams);
  }

  handleDeleteFilePermanent(file) {
    const {
      intl,
      openConfirmationModal,
      deleteFilePermanentAction,
    } = this.props;

    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      warning: intl.formatMessage({
        defaultMessage: 'You are permanently deleting this file.',
      }),
      text: intl.formatMessage({
        defaultMessage: 'If you delete this file it will be deleted permanently from your workspace and all Collections it may be a part of - this can not be undone!',
      }),
      confirmText: intl.formatMessage({ defaultMessage: 'Delete permanently' }),
      processingText: intl.formatMessage({ defaultMessage: 'Deleting' }),
      onConfirm: () => deleteFilePermanentAction(file.uuid),
    };
    openConfirmationModal(file, modalParams);
  }

  handleChangeWatermarked(file) {
    const { updateFileAction } = this.props;

    this.handleWatermarkedChangeLoading(true);

    APIcreateFilesGroup({ type: 'edit_files' }).then((response) => {
      const { data } = response;
      if (data) {
        const newFile = file;
        newFile.watermarked = !newFile.watermarked;
        newFile.uploadUuid = data.uuid;
        return updateFileAction(file).then(() => {
          this.handleWatermarkedChangeLoading(false);
        });
      }
      return null;
    });
  }

  handleWatermarkedChangeLoading(value) {
    this.setState({ watermarkedChangeLoading: value });
  }

  render() {
    const {
      intl,
      history,
      profile,
      file,
      collection,
      sidebarCollection,
      handleAddToCollection,
      handleAddToSidebarCollection,
      handleRemoveFromSidebarCollection,
      handleMoveToCollection,
      handleEditFile,
      handleDownloadFile,
      handleDownloadShareFile,
      handleDownloadRequestFile,
      handleRequestDownloadFile,
      handleShareFile,
      handleChangeOwner,
      handleChangeAccess,
      handleChangeStatus,
      handleArchive,
      handleSetCollectionFeaturedImage,
      handleChangeThumbnail,
      handleRefreshThumbnail,
      handleRemoveThumbnail,
      openChangeThumbnailModal,
      shareUuid,
      share,
      inModal,
      filesView,
      fileVersion,
    } = this.props;
    const { watermarkedChangeLoading } = this.state;
    const watermarkEnabled = !!profile?.client?.watermark_url;
    const suffix = inModal ? '-m' : '';
    const tooltipParams = {
      fade: false,
      placement: 'bottom',
      trigger: 'hover',
    };

    if (filesView === 'grid') {
      return file.deleted_at ? (
        (userCan('file.delete', file) && (
          <ul className="grid-item-actions-list">
            <li>
              <a
                href="#restore-from-trash"
                id={`FileActionRestoreFromTrash-${file.uuid}${suffix}`}
                onClick={(e) => {
                  e.preventDefault();
                  this.handleRestoreDeletedFile(file);
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Restore' })}
              >
                <i className="far fa-fw fa-trash-undo" />
              </a>
              <UncontrolledTooltip target={`FileActionRestoreFromTrash-${file.uuid}${suffix}`} {...tooltipParams}>
                {intl.formatMessage({ defaultMessage: 'Restore' })}
              </UncontrolledTooltip>
            </li>
            {userHasCap('delete_files_collections_permanent') && (
              <li>
                <a
                  href="#delete-permanently"
                  id={`FileActionDeletePermanently-${file.uuid}${suffix}`}
                  onClick={(e) => {
                    e.preventDefault();
                    this.handleDeleteFilePermanent(file);
                  }}
                  aria-label={intl.formatMessage({ defaultMessage: 'Delete permanently' })}
                >
                  <i className="fas fa-fw fa-trash" />
                </a>
                <UncontrolledTooltip target={`FileActionDeletePermanently-${file.uuid}${suffix}`} {...tooltipParams}>
                  {intl.formatMessage({ defaultMessage: 'Delete permanently' })}
                </UncontrolledTooltip>
              </li>
            )}
          </ul>
        ))
      ) : (
        <ul className="grid-item-actions-list">
          {(!shareUuid && (!file.archived || getSetting('cs1')) && isEditionInWorkspace(file)) && (
            <>
              {(sidebarCollection && !file.archived && sidebarCollection.open) && (
                <li>
                  {this.isFileInSidebarCollection(file.uuid) ? (
                    <>
                      <a
                        href="#remove-from-sidebar-collection"
                        id={`FileActionCollectionSidebar-${file.uuid}${suffix}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveFromSidebarCollection(sidebarCollection.uuid, file.uuid);
                        }}
                        aria-label={intl.formatMessage({
                          defaultMessage: 'Remove from {sidebarCollectionName}',
                        }, { sidebarCollectionName: sidebarCollection.name })}
                      >
                        <i className="far fa-folder-minus" />
                      </a>
                      <UncontrolledTooltip target={`FileActionCollectionSidebar-${file.uuid}${suffix}`} {...tooltipParams}>
                        {intl.formatMessage({
                          defaultMessage: 'Remove from {sidebarCollectionName}',
                        }, { sidebarCollectionName: sidebarCollection.name })}
                      </UncontrolledTooltip>
                    </>
                  ) : (
                    <>
                      <a
                        href="#add-to-sidebar-collection"
                        id={`FileActionCollectionSidebar-${file.uuid}${suffix}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAddToSidebarCollection(sidebarCollection.uuid, file);
                        }}
                        aria-label={intl.formatMessage({
                          defaultMessage: 'Add to {sidebarCollectionName}',
                        }, { sidebarCollectionName: sidebarCollection.name })}
                      >
                        <i className="far fa-folder-plus" />
                      </a>
                      <UncontrolledTooltip target={`FileActionCollectionSidebar-${file.uuid}${suffix}`} {...tooltipParams}>
                        {intl.formatMessage({
                          defaultMessage: 'Add to {sidebarCollectionName}',
                        }, { sidebarCollectionName: sidebarCollection.name })}
                      </UncontrolledTooltip>
                    </>
                  )}
                </li>
              )}
              {userCan('add_files_to_collections') && (
                <li>
                  <a
                    href="#add-to-collection"
                    id={`FileActionCollection-${file.uuid}${suffix}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddToCollection(file);
                    }}
                    aria-label={intl.formatMessage({ defaultMessage: 'Add to a Collection' })}
                  >
                    <i className="far fa-layer-plus" />
                  </a>
                  <UncontrolledTooltip target={`FileActionCollection-${file.uuid}${suffix}`} {...tooltipParams}>
                    {intl.formatMessage({ defaultMessage: 'Add to a Collection' })}
                  </UncontrolledTooltip>
                </li>
              )}
            </>
          )}
          {(!shareUuid && !userCan('file.download', file) && userCan('file.request_download', file) && file.request) && (
            <li>
              <a
                href="#download-request"
                id={`FileActionDownloadRequest-${file.uuid}${suffix}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleDownloadRequestFile(file);
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Download' })}
              >
                <i className="far fa-download" />
              </a>
              <UncontrolledTooltip target={`FileActionDownloadRequest-${file.uuid}${suffix}`} {...tooltipParams}>
                {intl.formatMessage({ defaultMessage: 'Download' })}
              </UncontrolledTooltip>
            </li>
          )}
          {(
            (share && share.allow_download && file.type !== 'link' && file.capabilities.includes('download_files_and_collections')) ||
            (!share && userCan('file.download', file))
          ) && (
            <li>
              <a
                href="#download"
                id={`FileActionDownload-${file.uuid}${suffix}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (shareUuid) {
                    handleDownloadShareFile(share, file);
                  } else {
                    handleDownloadFile(file, fileVersion);
                  }
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Download' })}
              >
                <i className="far fa-download" />
              </a>
              <UncontrolledTooltip target={`FileActionDownload-${file.uuid}${suffix}`} {...tooltipParams}>
                {intl.formatMessage({ defaultMessage: 'Download' })}
              </UncontrolledTooltip>
            </li>
          )}
          {(
            share &&
            share.allow_download &&
            getSetting('cs1') &&
            userHasFeature('public-file-urls')
          ) && (
            <li>
              <a
                href="#copy-public-url"
                id={`FileActionCopyPublicUrl-${file.uuid}${suffix}`}
                onClick={(e) => {
                  const handler = () => {
                    e.preventDefault();
                    APIgetShareFilePublicUrl(shareUuid, file.uuid).then((response) => {
                      const { data } = response;
                      if (data && data.url) {
                        copy(data.url);
                        addFlashMessage({
                          type: 'success',
                          text: intl.formatMessage({ defaultMessage: 'File public URL has been copied' }),
                        });
                      }
                    });
                  };

                  upgradeModalIfNeeded('public-file-urls', () => handler(e), e);
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Copy public URL' })}
              >
                <i className="far fa-copy" />
              </a>
              <UncontrolledTooltip target={`FileActionCopyPublicUrl-${file.uuid}${suffix}`} {...tooltipParams}>
                {intl.formatMessage({ defaultMessage: 'Copy public URL' })}
                <UpgradePlanButton
                  feature="public-file-urls"
                  simple={true}
                />
              </UncontrolledTooltip>
            </li>
          )}
          {(!shareUuid && !userCan('file.download', file) && userCan('file.request_download', file) && !file.request) && (
            <li>
              <a
                href="#request-download"
                id={`FileActionRequestDownload-${file.uuid}${suffix}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleRequestDownloadFile(file);
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Request Download' })}
              >
                <i className="far fa-cart-arrow-down" />
              </a>
              <UncontrolledTooltip target={`FileActionRequestDownload-${file.uuid}${suffix}`} {...tooltipParams}>
                {intl.formatMessage({ defaultMessage: 'Request Download' })}
              </UncontrolledTooltip>
            </li>
          )}
          <ErrorBoundary invisible={true}>
            {(!shareUuid && userCan('file.share', file) && (!file.archived || getSetting('cs1'))) ? (
              <li>
                <a
                  href="#share"
                  id={`FileActionShare-${file.uuid}${suffix}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (userCan('file.change_access', file)) {
                      handleChangeAccess(file.uuid);
                    } else {
                      handleShareFile(file);
                    }
                  }}
                  aria-label={userCan('file.change_access', file) ? (
                    intl.formatMessage({ defaultMessage: 'Access & Share' })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Share a link' })
                  )}
                >
                  <i className="far fa-share-alt" />
                </a>
                <UncontrolledTooltip target={`FileActionShare-${file.uuid}${suffix}`} {...tooltipParams}>
                  {userCan('file.change_access', file) ? (
                    intl.formatMessage({ defaultMessage: 'Access & Share' })
                  ) : (
                    intl.formatMessage({ defaultMessage: 'Share a link' })
                  )}
                </UncontrolledTooltip>
              </li>
            ) : (
              <>
                {(!shareUuid && userCan('file.change_access', file)) && (
                  <li>
                    <a
                      href="#share"
                      id={`FileActionShare-${file.uuid}${suffix}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleChangeAccess(file.uuid);
                      }}
                      aria-label={intl.formatMessage({ defaultMessage: 'Access & Share' })}
                    >
                      <i className="far fa-share-alt" />
                    </a>
                    <UncontrolledTooltip target={`FileActionShare-${file.uuid}${suffix}`} {...tooltipParams}>
                      {intl.formatMessage({ defaultMessage: 'Access & Share' })}
                    </UncontrolledTooltip>
                  </li>
                )}
              </>
            )}
          </ErrorBoundary>
          {(file.type !== 'link' && (inModal && file.urls.print && ((share && share.allow_download) || (!share && userCan('file.download', file))))) && (
            <li>
              <a
                href="#print"
                id={`FileActionPrint-${file.uuid}${suffix}`}
                onClick={(e) => {
                  e.preventDefault();
                  printFile(file.urls.print);
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Print' })}
              >
                <i className="far fa-print" />
              </a>
              <UncontrolledTooltip target={`FileActionPrint-${file.uuid}${suffix}`} {...tooltipParams}>
                {intl.formatMessage({ defaultMessage: 'Print' })}
              </UncontrolledTooltip>
            </li>
          )}
          {(shareUuid && share && share.status_change) && (
            <li>
              <a
                href="#status-change"
                id="status-change"
                onClick={(e) => {
                  e.preventDefault();
                  handleChangeStatus([file]);
                }}
                aria-label={intl.formatMessage({ defaultMessage: 'Change Status' })}
              >
                <i className="far fa-circle-dot" />
              </a>
              <UncontrolledTooltip
                target="status-change"
                {...tooltipParams}
              >
                {intl.formatMessage({ defaultMessage: 'Change Status' })}
              </UncontrolledTooltip>
            </li>
          )}
          {!shareUuid && (
            <li className="action-more">
              {!inModal && (
                <FileExpirationIcon file={file} />
              )}
              <UncontrolledDropdown>
                <DropdownToggle nav className="options" aria-label={intl.formatMessage({ defaultMessage: 'More actions' })}>
                  <i className="far fa-ellipsis-h" />
                </DropdownToggle>
                <DropdownMenu right container="body">
                  {file.type === 'link' && (
                    <DropdownItem
                      onClick={() => {
                        history.push({
                          pathname: `/files/${file.uuid}`,
                          state: {
                            modal: true,
                          },
                        });
                      }}
                    >
                      <i className="fas fa-link-horizontal" />
                      {intl.formatMessage({ defaultMessage: 'View details' })}
                    </DropdownItem>
                  )}
                  {(userCan('file.edit', file) && isEditionInWorkspace(file)) && (
                    <DropdownItem
                      onClick={() => {
                        handleEditFile(file);
                      }}
                    >
                      <i className="fas fa-pencil" />
                      {intl.formatMessage({ defaultMessage: 'Edit' })}
                    </DropdownItem>
                  )}
                  {userCan('file.delete', file) && (
                    <DropdownItem
                      onClick={() => {
                        this.handleDeleteFile(file);
                      }}
                    >
                      <i className="fas fa-trash" />
                      {intl.formatMessage({ defaultMessage: 'Delete' })}
                    </DropdownItem>
                  )}
                  {(collection && userCan('collection.remove', collection) && (!isArchive() || getSetting('cs1'))) && (
                    <>
                      {(collection.open && !collection.smart) && (
                        <DropdownItem
                          onClick={() => {
                            this.handleRemoveFromCollection(file);
                          }}
                        >
                          <i className="fas fa-layer-minus" />
                          {intl.formatMessage({ defaultMessage: 'Remove from this Collection' })}
                        </DropdownItem>
                      )}
                      {(collection.open && userCan('add_files_to_collections')) && (
                        <DropdownItem
                          onClick={() => {
                            handleMoveToCollection(file);
                          }}
                        >
                          <i className="fas fa-arrow-alt-right" />
                          {intl.formatMessage({ defaultMessage: 'Move to Collection' })}
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {(userCan('file.change_owner', file) && isEditionInWorkspace(file)) && (
                    <DropdownItem
                      onClick={() => {
                        handleChangeOwner(file.uuid);
                      }}
                    >
                      <i className="fas fa-user-circle" />
                      {intl.formatMessage({ defaultMessage: 'Change Owner' })}
                    </DropdownItem>
                  )}
                  {(userCan('file.edit', file) && isEditionInWorkspace(file)) && (
                    <>
                      <DropdownItem
                        className={classnames({ 'inactive silent-pointer-events': !userHasFeature('file-status') })}
                        onClick={(e) => upgradeModalIfNeeded('file-status', () => handleChangeStatus([file]), e)}
                      >
                        <i className="far fa-circle-dot" />
                        {intl.formatMessage({ defaultMessage: 'Change Status' })}
                        <UpgradePlanButton
                          feature="file-status"
                          simple={true}
                        />
                      </DropdownItem>
                      {(file.type_group !== 'image') && (
                        <DropdownItem
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('custom-file-thumbnail') })}
                          onClick={(e) => {
                            const handler = () => {
                              openChangeThumbnailModal({
                                className: 'modal-select-file modal-select-file-enhanced',
                                handleSelectFile: (thumbnail) => {
                                  handleChangeThumbnail(file, thumbnail);
                                },
                                showRemoveButton: true,
                                removeButtonText: intl.formatMessage({ defaultMessage: 'Remove thumbnail' }),
                                handleRemove: () => handleRemoveThumbnail(file),
                              });
                            };
                            upgradeModalIfNeeded('custom-file-thumbnail', handler, e);
                          }}
                        >
                          <i className="fas fa-image" />
                          {intl.formatMessage({ defaultMessage: 'Change thumbnail' })}
                          <UpgradePlanButton
                            feature="custom-file-thumbnail"
                            simple={true}
                          />
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {(file.type !== 'link' && collection && userCan('collection.edit', collection)) && (
                    <DropdownItem
                      onClick={() => {
                        handleSetCollectionFeaturedImage(collection.uuid, file.uuid);
                      }}
                      disabled={
                        collection.featured_file &&
                        collection.featured_file.uuid === file.uuid
                      }
                    >
                      <i className="fas fa-folder-image" />
                      {intl.formatMessage({ defaultMessage: 'Set as Collection thumbnail' })}
                    </DropdownItem>
                  )}
                  {file.can_have_thumbnail && (
                    <DropdownItem
                      onClick={() => {
                        handleRefreshThumbnail(file);
                      }}
                    >
                      <i className="fas fa-rotate-right" />
                      {intl.formatMessage({ defaultMessage: 'Refresh thumbnail' })}
                    </DropdownItem>
                  )}
                  <CopyToClipboard
                    text={file.uuid}
                    options={{
                      format: 'text/plain',
                    }}
                  >
                    <DropdownItem
                      onClick={() => {
                        addFlashMessage({
                          type: 'success',
                          text: intl.formatMessage({ defaultMessage: 'File ID has been copied' }),
                        });
                      }}
                    >
                      <i className="fas fa-copy" />
                      {intl.formatMessage({ defaultMessage: 'Copy ID' })}
                    </DropdownItem>
                  </CopyToClipboard>
                  {((
                    getSetting('cs1') ||
                    getSetting('allow_public_urls') ||
                    (userHasCap('public_file_urls') && (
                      getSetting('public_file_urls') ||
                      !userHasFeature('public_file_urls')
                    ))
                  ) && userCan('file.download', file) && isEditionInWorkspace(file)) && (
                    <DropdownItem
                      className={classnames({ 'inactive silent-pointer-events': !userHasFeature('public-file-urls') })}
                      onClick={(e) => upgradeModalIfNeeded('public-file-urls', () => this.handleCopyFilePublicUrl(file), e)}
                    >
                      <i className="fas fa-copy" />
                      {intl.formatMessage({ defaultMessage: 'Copy public URL' })}
                      <UpgradePlanButton
                        feature="public-file-urls"
                        simple={true}
                      />
                    </DropdownItem>
                  )}
                  {(userCan('file.edit', file) && file.can_be_watermarked) && (
                    <DropdownItem
                      onClick={(e) => {
                        const handler = () => {
                          if (watermarkEnabled) {
                            this.handleChangeWatermarked(file);
                          }
                        };

                        upgradeModalIfNeeded('watermarking', handler, e);
                      }}
                      id={`FileActionWatermark-${file.uuid}`}
                      className={classnames('watermark-dropdown dropdown-with-notice', {
                        inactive: watermarkedChangeLoading || !watermarkEnabled || !userHasFeature('watermarking'),
                        'silent-pointer-events': !userHasFeature('watermarking'),
                      })}
                    >
                      <div>
                        {!watermarkedChangeLoading && (
                          <i className="far fa-stamp" />
                        )}
                        {watermarkedChangeLoading && (
                          <ProcessingSpinner show={watermarkedChangeLoading} />
                        )}
                        {(file.watermarked && watermarkEnabled) ?
                          intl.formatMessage({ defaultMessage: 'Remove watermark' }) :
                          intl.formatMessage({ defaultMessage: 'Add watermark' })}
                        <UpgradePlanButton
                          feature="watermarking"
                          simple={true}
                        />
                      </div>
                        {((userIsAdmin() || userIsManager()) && !watermarkEnabled) && (
                          <div className="dropdown-notice">
                            {intl.formatMessage({ defaultMessage: 'Add a watermark in your <link>workspace settings</link>' }, {
                              link: (msg) => (
                                <Link to="/admin/workspace" className="prevent-upgrade-modal">
                                  {msg}
                                </Link>
                              ),
                            })}
                          </div>
                        )}
                    </DropdownItem>
                  )}
                  <FileActionsIntegrations files={[file]} />
                  {(userCan('file.archive') && isEditionInWorkspace(file)) && (
                    <>
                      <DropdownItem divider />
                      {file.archived ? (
                        <DropdownItem
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('archive') })}
                          onClick={(e) => upgradeModalIfNeeded('archive', () => handleArchive(file), e)}
                        >
                          <i className="fas fa-box-open" />
                          {intl.formatMessage({ defaultMessage: 'Unarchive' })}
                          <UpgradePlanButton
                            feature="archive"
                            simple={true}
                          />
                        </DropdownItem>
                      ) : (
                        <DropdownItem
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('archive') })}
                          onClick={(e) => upgradeModalIfNeeded('archive', () => handleArchive(file), e)}
                        >
                          <i className="fas fa-archive" />
                          {intl.formatMessage({ defaultMessage: 'Archive' })}
                          <UpgradePlanButton
                            feature="archive"
                            simple={true}
                          />
                        </DropdownItem>
                      )}
                    </>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </li>
          )}
        </ul>
      );
    }

    if (filesView === 'list' || filesView === 'tile') {
      const fileLink = {
        pathname: `/files/${file.uuid}`,
        hash: '#comments',
        state: {
          modal: true,
        },
      };

      return (
        <div className="action-more-icons">
          {filesView === 'list' && (
            <>
              <GridItemLikes
                file={file}
                readOnly={!!file.deleted_at}
              />
              {userCanSeeComments() && (
                <li className="navigation-file-comments">
                  <Link to={fileLink} className="file-comments" id={`FileCommentsBtn-${file.uuid}`}>
                    <span className="file-comments-btn">
                      <i className="fas fa-comment" />
                    </span>
                    <span className="file-comments-no">
                      <span>{file.comments_total}</span>
                    </span>
                  </Link>
                  <UncontrolledTooltip fade={false} placement="bottom" target={`FileCommentsBtn-${file.uuid}`}>
                    {intl.formatMessage({ defaultMessage: 'Show comments' })}
                  </UncontrolledTooltip>
                </li>
              )}
            </>
          )}
          <FileExpirationIcon file={file} />
          {file.deleted_at ? (
            ((userCan('file.delete', file)) && (
              <UncontrolledDropdown>
                <DropdownToggle nav className="options" aria-label={intl.formatMessage({ defaultMessage: 'Actions' })}>
                  <i className="far fa-ellipsis-h" />
                </DropdownToggle>
                <DropdownMenu right container="body" className="list-item-actions-list">
                  <DropdownItem
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleRestoreDeletedFile(file);
                    }}
                  >
                    <i className="far fa-fw fa-trash-undo" />
                    {intl.formatMessage({ defaultMessage: 'Restore' })}
                  </DropdownItem>
                  {userHasCap('delete_files_collections_permanent') && (
                    <DropdownItem
                      onClick={(e) => {
                        e.preventDefault();
                        this.handleDeleteFilePermanent(file);
                      }}
                    >
                      <i className="fas fa-fw fa-trash" />
                      {intl.formatMessage({ defaultMessage: 'Delete permanently' })}
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            ))) : (
              <UncontrolledDropdown>
                <DropdownToggle nav className="options" aria-label={intl.formatMessage({ defaultMessage: 'Actions' })}>
                  <i className="far fa-ellipsis-h" />
                </DropdownToggle>
                <DropdownMenu right container="body" className="list-item-actions-list">
                  {file.type === 'link' && (
                    <DropdownItem
                      onClick={() => {
                        history.push({
                          pathname: `/files/${file.uuid}`,
                          state: {
                            modal: true,
                          },
                        });
                      }}
                    >
                      <i className="fas fa-link-horizontal" />
                      {intl.formatMessage({ defaultMessage: 'View details' })}
                    </DropdownItem>
                  )}
                  {(!shareUuid && (!file.archived || getSetting('cs1'))) && (
                    <>
                      {(sidebarCollection && !file.archived && sidebarCollection.open) && (
                        <>
                          {this.isFileInSidebarCollection(file.uuid) ? (
                            <DropdownItem
                              onClick={() => {
                                handleRemoveFromSidebarCollection(
                                  sidebarCollection.uuid,
                                  file.uuid,
                                );
                              }}
                            >
                              <i className="fas fa-folder-minus" />
                              {intl.formatMessage({
                                defaultMessage: 'Remove from {sidebarCollectionName}',
                              }, { sidebarCollectionName: sidebarCollection.name })}
                            </DropdownItem>
                          ) : (
                            <DropdownItem
                              onClick={() => {
                                handleAddToSidebarCollection(sidebarCollection.uuid, file);
                              }}
                            >
                              <i className="fas fa-folder-plus" />
                              {intl.formatMessage({
                                defaultMessage: 'Add to {sidebarCollectionName}',
                              }, { sidebarCollectionName: sidebarCollection.name })}
                            </DropdownItem>
                          )}
                        </>
                      )}
                      {userCan('add_files_to_collections') && (
                        <DropdownItem
                          onClick={() => {
                            handleAddToCollection(file);
                          }}
                        >
                          <i className="fas fa-layer-plus" />
                          {intl.formatMessage({ defaultMessage: 'Add to a Collection' })}
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {(!share && collection && collection.open && !collection.smart && userCan('collection.remove', collection) && (!isArchive() || getSetting('cs1'))) && (
                    <DropdownItem
                      onClick={() => {
                        this.handleRemoveFromCollection(file);
                      }}
                    >
                      <i className="fas fa-layer-minus" />
                      {intl.formatMessage({ defaultMessage: 'Remove from this Collection' })}
                    </DropdownItem>
                  )}
                  {(!shareUuid && !userCan('file.download', file) && userCan('file.request_download', file) && file.request) && (
                    <DropdownItem
                      onClick={() => {
                        handleDownloadRequestFile(file);
                      }}
                    >
                      <i className="fas fa-download" />
                      {intl.formatMessage({ defaultMessage: 'Download' })}
                    </DropdownItem>
                  )}
                  {(
                    (share && share.allow_download && file.type !== 'link' && file.capabilities.includes('download_files_and_collections')) ||
                    (!share && userCan('file.download', file))
                  ) && (
                    <DropdownItem
                      onClick={() => {
                        if (shareUuid) {
                          handleDownloadShareFile(share, file);
                        } else {
                          handleDownloadFile(file, fileVersion);
                        }
                      }}
                    >
                      <i className="fas fa-download" />
                      {intl.formatMessage({ defaultMessage: 'Download' })}
                    </DropdownItem>
                  )}
                  {(!shareUuid && !userCan('file.download', file) && userCan('file.request_download', file) && !file.request) && (
                    <DropdownItem
                      onClick={() => {
                        handleRequestDownloadFile(file);
                      }}
                    >
                      <i className="fas fa-cart-arrow-down" />
                      {intl.formatMessage({ defaultMessage: 'Request Download' })}
                    </DropdownItem>
                  )}
                  {(!shareUuid && userCan('file.share', file) && (!file.archived || getSetting('cs1'))) && (
                    <DropdownItem
                      onClick={() => {
                        if (userCan('file.change_access', file)) {
                          handleChangeAccess(file.uuid);
                        } else {
                          handleShareFile(file);
                        }
                      }}
                    >
                      <i className="fas fa-share-alt" />
                      {userCan('file.change_access', file) ? (
                        intl.formatMessage({ defaultMessage: 'Access & Share' })
                      ) : (
                        intl.formatMessage({ defaultMessage: 'Share a link' })
                      )}
                    </DropdownItem>
                  )}
                  {!share && (
                    <>
                      {userCan('file.edit', file) && (
                        <DropdownItem
                          onClick={() => {
                            handleEditFile(file);
                          }}
                        >
                          <i className="fas fa-pencil" />
                          {intl.formatMessage({ defaultMessage: 'Edit' })}
                        </DropdownItem>
                      )}
                      {userCan('file.change_owner', file) && (
                        <DropdownItem
                          onClick={() => {
                            handleChangeOwner(file.uuid);
                          }}
                        >
                          <i className="fas fa-user-circle" />
                          {intl.formatMessage({ defaultMessage: 'Change Owner' })}
                        </DropdownItem>
                      )}
                      {userCan('file.edit', file) && (
                        <DropdownItem
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('file-status') })}
                          onClick={(e) => upgradeModalIfNeeded('file-status', () => handleChangeStatus([file]), e)}
                        >
                          <i className="far fa-circle-dot" />
                          {intl.formatMessage({ defaultMessage: 'Change Status' })}
                          <UpgradePlanButton
                            feature="file-status"
                            simple={true}
                          />
                        </DropdownItem>
                      )}
                      {userCan('file.delete', file) && (
                        <DropdownItem
                          onClick={() => {
                            this.handleDeleteFile(file);
                          }}
                        >
                          <i className="fas fa-trash" />
                          {intl.formatMessage({ defaultMessage: 'Delete' })}
                        </DropdownItem>
                      )}
                      {(collection && userCan('collection.edit', collection)) && (
                        <DropdownItem
                          onClick={() => {
                            handleSetCollectionFeaturedImage(collection.uuid, file.uuid);
                          }}
                          disabled={
                            collection.featured_file &&
                            collection.featured_file.uuid === file.uuid
                          }
                        >
                          <i className="fas fa-folder-image" />
                          {intl.formatMessage({ defaultMessage: 'Set as Collection thumbnail' })}
                        </DropdownItem>
                      )}
                      {(userCan('file.edit', file) && (file.type_group !== 'image')) && (
                        <DropdownItem
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('custom-file-thumbnail') })}
                          onClick={(e) => {
                            const handler = () => {
                              openChangeThumbnailModal({
                                className: 'modal-select-file modal-select-file-enhanced',
                                handleSelectFile: (thumbnail) => {
                                  handleChangeThumbnail(file, thumbnail);
                                },
                                showRemoveButton: true,
                                removeButtonText: 'Remove thumbnail',
                                handleRemove: () => handleRemoveThumbnail(file),
                              });
                            };

                            upgradeModalIfNeeded('custom-file-thumbnail', handler, e);
                          }}
                          disabled={!userHasFeature('custom-file-thumbnail')}
                        >
                          <i className="fas fa-image" />
                          {intl.formatMessage({ defaultMessage: 'Change thumbnail' })}
                          <UpgradePlanButton
                            feature="custom-file-thumbnail"
                            simple={true}
                          />
                        </DropdownItem>
                      )}
                      {file.can_have_thumbnail && (
                        <DropdownItem
                          onClick={() => {
                            handleRefreshThumbnail(file);
                          }}
                        >
                          <i className="fas fa-rotate-right" />
                          {intl.formatMessage({ defaultMessage: 'Refresh thumbnail' })}
                        </DropdownItem>
                      )}
                      <CopyToClipboard
                        text={file.uuid}
                        options={{
                          format: 'text/plain',
                        }}
                      >
                        <DropdownItem
                          onClick={() => {
                            addFlashMessage({
                              type: 'success',
                              text: intl.formatMessage({ defaultMessage: 'File ID has been copied' }),
                            });
                          }}
                        >
                          <i className="fas fa-copy" />
                          {intl.formatMessage({ defaultMessage: 'Copy ID' })}
                        </DropdownItem>
                      </CopyToClipboard>
                      {((
                        getSetting('cs1') ||
                        getSetting('allow_public_urls') ||
                        (userHasCap('public_file_urls') && (
                          getSetting('public_file_urls') ||
                          !userHasFeature('public_file_urls')
                        ))
                      ) && userCan('file.download', file)) && (
                        <DropdownItem
                          className={classnames({ 'inactive silent-pointer-events': !userHasFeature('public-file-urls') })}
                          onClick={(e) => upgradeModalIfNeeded('public-file-urls', () => this.handleCopyFilePublicUrl(file), e)}
                        >
                          <i className="fas fa-copy" />
                          {intl.formatMessage({ defaultMessage: 'Copy public URL' })}
                          <UpgradePlanButton
                            feature="public-file-urls"
                            simple={true}
                          />
                        </DropdownItem>
                      )}
                      {(userCan('file.edit', file) && file.can_be_watermarked) && (
                        <DropdownItem
                          onClick={(e) => {
                            const handler = () => {
                              if (watermarkEnabled) {
                                this.handleChangeWatermarked(file);
                              }
                            };
                            upgradeModalIfNeeded('watermarking', handler, e);
                          }}
                          id={`FileActionWatermark-${file.uuid}`}
                          className={classnames('watermark-dropdown dropdown-with-notice', {
                            inactive: watermarkedChangeLoading || !watermarkEnabled || !userHasFeature('watermarking'),
                            'silent-pointer-events': watermarkedChangeLoading || !watermarkEnabled || !userHasFeature('watermarking'),
                          })}
                        >
                          <div>
                            {!watermarkedChangeLoading && (
                              <i className="far fa-stamp" />
                            )}
                            {watermarkedChangeLoading && (
                              <ProcessingSpinner show={watermarkedChangeLoading} />
                            )}
                            {(file.watermarked && watermarkEnabled) ?
                              intl.formatMessage({ defaultMessage: 'Remove watermark' }) :
                              intl.formatMessage({ defaultMessage: 'Add watermark' })}
                            <UpgradePlanButton
                              feature="watermarking"
                              simple={true}
                            />
                          </div>
                          {((userIsAdmin() || userIsManager()) && !watermarkEnabled) && (
                            <div className="dropdown-notice">
                              {intl.formatMessage({ defaultMessage: 'Add a watermark in your <link>workspace settings</link>' }, {
                                link: (msg) => (
                                  <Link to="/admin/workspace" className="prevent-upgrade-modal">
                                    {msg}
                                  </Link>
                                ),
                              })}
                            </div>
                          )}
                        </DropdownItem>
                      )}
                      <FileActionsIntegrations files={[file]} />
                      {userCan('file.archive') && (
                        <>
                          <DropdownItem divider />
                          {file.archived ? (
                            <DropdownItem
                              className={classnames({ 'inactive silent-pointer-events': !userHasFeature('archive') })}
                              onClick={(e) => upgradeModalIfNeeded('archive', () => handleArchive(file), e)}
                            >
                              <i className="fas fa-box-open" />
                              {intl.formatMessage({ defaultMessage: 'Unarchive' })}
                              <UpgradePlanButton
                                feature="archive"
                                simple={true}
                              />
                            </DropdownItem>
                          ) : (
                            <DropdownItem
                              className={classnames({ 'inactive silent-pointer-events': !userHasFeature('archive') })}
                              onClick={(e) => upgradeModalIfNeeded('archive', () => handleArchive(file), e)}
                            >
                              <i className="fas fa-archive" />
                              {intl.formatMessage({ defaultMessage: 'Archive' })}
                              <UpgradePlanButton
                                feature="archive"
                                simple={true}
                              />
                            </DropdownItem>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {(share && share.status_change) && (
                    <DropdownItem
                      onClick={() => {
                        handleChangeStatus([file]);
                      }}
                      disabled={!userHasFeature('file-status')}
                    >
                      <i className="far fa-circle-dot" />
                      {intl.formatMessage({ defaultMessage: 'Change Status' })}
                      <UpgradePlanButton
                        feature="file-status"
                        simple={true}
                      />
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
          )}
        </div>
      );
    }

    return null;
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  selectedFiles: state.Files.selectedFiles,
  collection: state.Collections.collection,
  sidebarCollection: state.Collections.sidebarCollection,
  sidebarCollectionFiles: state.Collections.sidebarCollectionFiles,
  sidebarCollectionOpen: state.Collections.sidebarCollectionOpen,
});

const mapDispatchToProps = (dispatch, props) => ({
  handleAddToCollection: (file) => {
    dispatch(setSelectedFile(file));
    dispatch(showCollectionAddModal());
  },
  handleAddToSidebarCollection: (collectionUuid, file) => {
    const { intl } = props;
    APIaddFilesToCollection(collectionUuid, { files: [file.uuid] }).then((response) => {
      dispatch(setSidebarCollection(response.data, false));
    });
    dispatch(addFilesToSidebarCollectionFilesList([file]));
    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'File has been added to Collection' }),
    });
  },
  handleRemoveFromSidebarCollection: (collectionUuid, file) => {
    const { intl } = props;
    dispatch(removeFilesFromCollection(collectionUuid, { files: [file] }));
    dispatch(removeFilesFromSidebarCollectionFilesList([{ uuid: file }]));
    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'File has been removed from Collection' }),
    });
  },
  handleMoveToCollection: (file) => {
    dispatch(setSelectedFile(file));
    dispatch(showCollectionAddModal(true));
  },
  openConfirmationModal: (file, params) => {
    dispatch(setSelectedFile(file));
    dispatch(showConfirmationModal(params));
  },
  deleteFilesFromCollection: (collectionUuid, params) => (
    APIcreateFilesGroup({
      type: 'collection_remove_files',
      data: { collection_uuid: collectionUuid },
    }).then((response) => {
      const fileGroupUuid = response.data.uuid;
      return dispatch(removeFilesFromCollection(collectionUuid, {
        ...params,
        files: params.selectedAllFiles ? [] : params.files,
        fileGroupUuid,
      })).then(() => {
        const { intl } = props;
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Files have been removed from Collection' }),
        });
        dispatch(removeFilesFromFilesList(params.files.map(f => ({ uuid: f }))));
      });
    })
  ),
  removeFile: fileUuid => (
    dispatch(deleteFile(fileUuid)).then(() => {
      const { intl } = props;
      const button = {
        url: '/trash',
        text: intl.formatMessage({ defaultMessage: 'Browse trash' }),
      };
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'File has been deleted' }),
        button,
      });
      dispatch(unsetSelectedFile());
    })
  ),
  restoreFile: fileUuid => (
    dispatch(restoreDeletedFile(fileUuid)).then(() => {
      const { intl } = props;
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'File has been restored from the trash' }),
      });
      dispatch(unsetSelectedFile());
    })
  ),
  deleteFilePermanentAction: fileUuid => (
    dispatch(deleteFilePermanent(fileUuid)).then(() => {
      const { intl } = props;
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'File has been deleted permanently' }),
      });
      dispatch(unsetSelectedFile());
    })
  ),
  unselectFile: () => (
    dispatch(unsetSelectedFile())
  ),
  handleEditFile: (file) => {
    dispatch(setSelectedFile(file));
    if (file.type === 'link') {
      dispatch(setUploadModalMode('link'));
    } else {
      dispatch(setUploadModalMode('edit'));
    }
    dispatch(showUploadModal());
  },
  handleDownloadFile: (file, versionId) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files: [file],
      versions: versionId ? [{
        uuid: file.uuid,
        version_id: versionId,
      }] : [],
      backdrop: true,
    }));
  },
  handleDownloadShareFile: (share, file) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files: [file],
      shareUuid: share.uuid,
      requireName: share.require_name,
      requireEmail: share.require_email,
      backdrop: true,
    }));
  },
  handleRequestDownloadFile: (file) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files: [file],
      backdrop: true,
      request: true,
    }));
  },
  handleChangeStatus: (files) => {
    dispatch(showModal('StatusModal', {
      className: 'status-change',
      files,
      backdrop: true,
      request: true,
    }));
  },
  handleDownloadRequestFile: (file) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files: [file],
      requestUuid: file.request.uuid,
      availableFileSizes: file.request.file_sizes.map(fs => (`${fs.id}`)),
      backdrop: true,
    }));
  },
  handleShareFile: (file) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      files: [file],
    }));
  },
  handleChangeOwner: (file) => {
    dispatch(showModal('ChangeOwnerModal', {
      className: 'modal-change-owner',
      files: [file],
    }));
  },
  handleChangeAccess: (file) => {
    dispatch(showModal('AccessModal', {
      className: 'modal-access modal-file-access',
      files: [file],
    }));
  },
  handleArchive: (file) => {
    dispatch(showModal('ArchiveModal', {
      className: 'modal-archive modal-file-archive',
      files: [file],
      action: file.archived ? 'unarchive' : 'archive',
    }));
  },
  handleSetCollectionFeaturedImage: (collectionUuid, fileUuid) => {
    APIsetCollectionFeaturedImage(collectionUuid, fileUuid).then((response) => {
      const { intl } = props;
      if (response.data) {
        dispatch(getCollection(response.data));
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'File has been set as Collection thumbnail' }),
        });
      } else {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      }
    });
  },
  handleChangeThumbnail: (file, thumbnailUuid) => {
    APIchangeFileThumbnail(file.uuid, thumbnailUuid).then((response) => {
      if (response.data) {
        dispatch(updateFilesOnFilesList([
          {
            ...file,
            attributes: response.data.attributes,
            urls: response.data.urls,
          },
        ]));
        addFlashMessage({
          type: 'success',
          text: 'File thumbnail changed',
        });
      } else {
        addFlashMessage({
          type: 'danger',
          text: 'Something went wrong',
        });
      }
    });
  },
  handleRefreshThumbnail: (file) => {
    APIrefreshFileThumbnail(file.uuid).then((response) => {
      const { intl } = props;
      if (response.data) {
        dispatch(updateFilesOnFilesList([
          {
            ...file,
            attributes: response.data.attributes,
            urls: response.data.urls,
            can_have_thumbnail: response.data.can_have_thumbnail,
          },
        ]));
        addFlashMessage({
          type: 'success',
          text: 'File thumbnail refreshed',
        });
      } else {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      }
    });
  },
  handleRemoveThumbnail: (file) => (
    APIremoveFileThumbnail(file.uuid).then((response) => {
      if (response.data) {
        dispatch(updateFilesOnFilesList([
          {
            ...file,
            attributes: response.data.attributes,
            urls: response.data.urls,
            can_have_thumbnail: response.data.can_have_thumbnail,
          },
        ]));
        addFlashMessage({
          type: 'success',
          text: 'File thumbnail removed',
        });
      } else {
        const { intl } = props;
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      }
    })
  ),
  updateFileAction: file => (
    dispatch(updateFile(file))
  ),
  openChangeThumbnailModal: (params) => {
    dispatch(showModalTop('FileSelectModalEnhanced', params));
  },
});

FileActions.propTypes = {
  intl: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  profile: PropTypes.object,
  file: PropTypes.object.isRequired,
  handleAddToCollection: PropTypes.func.isRequired,
  handleAddToSidebarCollection: PropTypes.func.isRequired,
  handleRemoveFromSidebarCollection: PropTypes.func.isRequired,
  handleMoveToCollection: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  deleteFilesFromCollection: PropTypes.func.isRequired,
  handleEditFile: PropTypes.func.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
  handleDownloadShareFile: PropTypes.func.isRequired,
  handleRequestDownloadFile: PropTypes.func.isRequired,
  handleDownloadRequestFile: PropTypes.func.isRequired,
  handleShareFile: PropTypes.func.isRequired,
  handleChangeOwner: PropTypes.func.isRequired,
  handleChangeAccess: PropTypes.func.isRequired,
  handleChangeStatus: PropTypes.func.isRequired,
  handleArchive: PropTypes.func.isRequired,
  handleSetCollectionFeaturedImage: PropTypes.func.isRequired,
  handleChangeThumbnail: PropTypes.func.isRequired,
  handleRefreshThumbnail: PropTypes.func.isRequired,
  handleRemoveThumbnail: PropTypes.func.isRequired,
  updateFileAction: PropTypes.func.isRequired,
  removeFile: PropTypes.func.isRequired,
  restoreFile: PropTypes.func.isRequired,
  deleteFilePermanentAction: PropTypes.func.isRequired,
  unselectFile: PropTypes.func.isRequired,
  openChangeThumbnailModal: PropTypes.func.isRequired,
  collection: PropTypes.object,
  sidebarCollection: PropTypes.object,
  sidebarCollectionFiles: PropTypes.array,
  shareUuid: PropTypes.string,
  share: PropTypes.object,
  inModal: PropTypes.bool,
  filesView: PropTypes.string,
  fileVersion: PropTypes.number,
};

FileActions.defaultProps = {
  profile: null,
  collection: null,
  sidebarCollection: null,
  sidebarCollectionFiles: [],
  shareUuid: null,
  share: null,
  inModal: false,
  filesView: 'grid',
  fileVersion: null,
};

export default injectIntl(withRouter(connect(mapStateToProps, mapDispatchToProps)(FileActions)));
